import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { PRESET_CUE_MESSAGE_PARAM } from '@/utils/cue-handoff';
import { openCueAndSend } from '@/utils/open-cue-and-send';

/**
 * One-shot landing path for `?preset_cue_message=...`.
 *
 * Cue is CSS-hidden on `/post-job` and `/reach`, so the wizard cannot open
 * the panel from those routes. The freemium dialog navigates to campaigns
 * (or the dashboard) with this query param; this hook opens Cue and sends
 * the message once the host is visible again.
 *
 * Cue thread state lives server-side (FountainAI listThreadsV2). The message
 * appends to whichever thread the bundle is already managing.
 *
 * `sentRef` latches after the first send so a search-param rerun cannot
 * double-send. The latch resets when the query param is gone, so a later
 * handoff in the same SPA session can send again. Pass `mountId: null`
 * while Cue is hidden so we do not toggle a widget the user cannot see.
 *
 * The host stays mounted across SPA navigations, so `location.search` is a
 * dependency — landing from `/post-job` does not remount this hook.
 */
export function usePresetCueMessage(mountId: string | null) {
  const sentRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const message = new URLSearchParams(location.search).get(
      PRESET_CUE_MESSAGE_PARAM,
    );
    if (!message) {
      sentRef.current = false;
      return undefined;
    }
    if (sentRef.current || !mountId) return undefined;

    return openCueAndSend(message, {
      mountId,
      onSent: () => {
        sentRef.current = true;

        const params = new URLSearchParams(location.search);
        params.delete(PRESET_CUE_MESSAGE_PARAM);
        const nextSearch = params.toString();
        navigate(
          `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}${
            location.hash
          }`,
          { replace: true },
        );
      },
    });
  }, [location.hash, location.pathname, location.search, mountId, navigate]);
}
